import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";

import classNames from "classnames";
import { isEqual } from "lodash";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import qs from "qs";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useQueryState } from "react-router-use-location-state";
import Select from "react-select";

import style from "../../assets/scss/components/search/search-results.module.scss";
import { DEFAULT_DATE_FORMAT } from "../../helpers/constants";
import { useHotkey, useHotkeyScope, hotkeys, hotkeyScopes } from "../../helpers/hotkeys";
import ArticleService from "../../services/ArticleService";
import { selectArticleFilterIsMobile } from "../../store/articleFilter/selector";
import {
  selectCalendarActiveNewsItemId,
  selectCalendarContentFilters,
  selectCalendarModalNewsItem,
  selectCalendarReadNewsItemIds
} from "../../store/calendar/selector";
import { setModalNewsItem, markNewsItemAsActive } from "../../store/calendar/slice";
import { selectUserArticleVersionPreference } from "../../store/user/selector";
import ArticleView from "../article/ArticleView";
import ArticleViewModal from "../article/ArticleViewModal";
import HorizontalInfiniteScroll from "../helpers/HorizontalInfiniteScroll";
import LoadingSpinner from "../LoadingSpinner";
import NewsItem from "../NewsItem";
import NewsItemSkeleton from "../NewsItemSkeleton";

import { formatSearchResult } from "./../../helpers/utils";

const CALENDAR_SERVICES = "newscalendar";

function CalendarSearchResults({ filters }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const articleService = new ArticleService();

  const readNewsItems = useSelector(selectCalendarReadNewsItemIds, isEqual);
  const activeNewsItemId = useSelector(selectCalendarActiveNewsItemId, isEqual);
  const modalNewsItem = useSelector(selectCalendarModalNewsItem, isEqual);
  const preferredArticleVersion = useSelector(selectUserArticleVersionPreference);
  const isMobile = useSelector(selectArticleFilterIsMobile, isEqual);
  const selectedContentFilters = useSelector(selectCalendarContentFilters, isEqual);

  const filterSearchStringCollection = filters
    .filter(filter => filter.calendarActive && !filter.default)
    .map(filter => filter?.properties?.searchString)
    .filter(searchString => !!searchString);
  const [searchResults, setSearchResults] = useState({
    totalResults: 0,
    data: [],
    loading: false
  });

  const activeNewsItem =
    searchResults.data && searchResults.data.length
      ? searchResults.data.find(item => item.id === activeNewsItemId)
      : null;

  const [sorting, setSorting] = useState("STARTDATE_ASC");
  const columnScrollableRef = useRef(null);

  const queryObject = qs.parse(search, { ignoreQueryPrefix: true });
  const searchString = queryObject.searchString ? queryObject.searchString : "";
  const startDate = queryObject.startDate ? queryObject.startDate : "";
  const endDate = queryObject.endDate ? queryObject.endDate : "";
  const [mediaTopics] = useQueryState("mediaTopics", []);
  const [regions] = useQueryState("regions", []);

  useHotkeyScope(hotkeyScopes.ARTICLE);

  useHotkey(hotkeys.NEXT_ARTICLE, () => {
    // if no article is selected, select the first article in the first column
    if (!activeNewsItemId) {
      if (!searchResults.data.length) return;
      return dispatch(markNewsItemAsActive({ id: searchResults.data[0].id }));
    }

    const nextActiveNewsItemIndex =
      searchResults.data.findIndex(article => article.id === activeNewsItemId) + 1;

    if (nextActiveNewsItemIndex > searchResults.data.length - 1) return;
    dispatch(markNewsItemAsActive({ id: searchResults.data[nextActiveNewsItemIndex].id }));
    return false;
  });

  useHotkey(hotkeys.PREVIOUS_ARTICLE, () => {
    if (!activeNewsItemId) {
      if (!searchResults.data.length) return;
      return dispatch(markNewsItemAsActive({ id: searchResults.data[0].id }));
    }

    const previousActiveNewsItemIndex =
      searchResults.data.findIndex(article => article.id === activeNewsItemId) - 1;

    if (previousActiveNewsItemIndex < 0) return;
    dispatch(markNewsItemAsActive({ id: searchResults.data[previousActiveNewsItemIndex].id }));
    return false;
  });

  useEffect(() => {
    // Reset active news item when entering or leaving the page
    dispatch(markNewsItemAsActive({ id: null, columnId: null }));
    return () => dispatch(markNewsItemAsActive({ id: null, columnId: null }));
  }, [dispatch, pathname]);

  useEffect(() => {
    const abortController = new AbortController();

    setSearchResults({
      loading: true
    });

    articleService
      .where(builder => {
        builder.service(CALENDAR_SERVICES);
        builder.searchQuery(searchString, filters, { ignoreService: true, calendarFilter: true });
        builder.sortOrder(sorting);
        builder.latest(preferredArticleVersion === "latest");
        builder.coverageTypes(selectedContentFilters);

        if (startDate) {
          if (!endDate) {
            // If no endDate is set we filter by setting the startDate to calendarStop
            // This will give us all events from the startDate and into the future in the results
            builder.calendarStop(
              DateTime.fromFormat(startDate, DEFAULT_DATE_FORMAT).toFormat("yyyy-LL-dd")
            );
          } else {
            builder.calendarStart(
              DateTime.fromFormat(startDate, DEFAULT_DATE_FORMAT).toFormat("yyyy-LL-dd")
            );
          }
        }

        if (endDate) {
          builder.calendarStop(
            DateTime.fromFormat(endDate, DEFAULT_DATE_FORMAT).toFormat("yyyy-LL-dd")
          );
        }

        if (mediaTopics.length > 0) {
          builder.mediaTopics(mediaTopics);
        }

        if (regions.length > 0) {
          builder.regions(regions);
        }
      })
      .offset(0)
      .showNumResults(80)
      .search(abortController)
      .then(response => {
        if (response) {
          setSearchResults({
            totalResults: response.numResults,
            data: response.data,
            loading: false
          });
        }
      });

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sorting,
    searchString,
    startDate,
    endDate,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(mediaTopics),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(regions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters),
    selectedContentFilters,
    preferredArticleVersion
  ]);

  const loadMore = () => {
    articleService
      .where(builder => {
        builder.service(CALENDAR_SERVICES);
        builder.searchQuery(searchString, filters, { ignoreService: true, calendarFilter: true });
        builder.sortOrder(sorting);
        builder.latest(preferredArticleVersion === "latest");
        builder.coverageTypes(selectedContentFilters);

        if (startDate) {
          if (!endDate) {
            // If no endDate is set we filter by setting the startDate to calendarStop
            // This will give us all events from the startDate and into the future in the results
            builder.calendarStop(
              DateTime.fromFormat(startDate, DEFAULT_DATE_FORMAT).toFormat("yyyy-LL-dd")
            );
          } else {
            builder.calendarStart(
              DateTime.fromFormat(startDate, DEFAULT_DATE_FORMAT).toFormat("yyyy-LL-dd")
            );
          }
        }

        if (endDate) {
          builder.calendarStop(
            DateTime.fromFormat(endDate, DEFAULT_DATE_FORMAT).toFormat("yyyy-LL-dd")
          );
        }
        if (mediaTopics.length > 0) {
          builder.mediaTopics(mediaTopics);
        }

        if (regions.length > 0) {
          builder.regions(regions);
        }
      })
      .offset(searchResults.data.length)
      .showNumResults(40)
      .search()
      .then(response => {
        setSearchResults({
          totalResults: response.numResults,
          data: [...searchResults.data, ...response.data],
          loading: false
        });
      });
  };

  useEffect(() => {
    if (isMobile && columnScrollableRef.current) {
      const hasVerticalScrollbar =
        columnScrollableRef.current.scrollHeight > columnScrollableRef.current.clientHeight;
      // Added the check because on iOS devices the scrollbar isn't visible
      const verticalScrollbarIsVisible =
        columnScrollableRef.current.offsetWidth > columnScrollableRef.current.clientWidth;
      columnScrollableRef.current.classList.toggle(
        style["column__scrollable--has-scrollbar"],
        hasVerticalScrollbar && verticalScrollbarIsVisible
      );
    }
  });

  const sortOptions = useMemo(
    () => [
      { value: "STARTDATE_ASC", label: t("articleFilter:Chronology") },
      {
        value: "UNIQUE_COVERAGE_TYPES_COUNT_DESC|STARTDATE_ASC",
        label: t("articleFilter:Priority")
      }
    ],
    [t]
  );

  const handleSortChange = sort => {
    if (sorting !== sort) {
      setSorting(sort);
    }
  };

  const getSelectedSortOption = useCallback(() => {
    return sortOptions.find(option => option.value === sorting) || sortOptions[0];
  }, [sorting]); // eslint-disable-line react-hooks/exhaustive-deps

  let selectedSortOption = getSelectedSortOption();

  const setActiveNewsItem = useCallback(
    (id, columnId) => {
      // Close newsItem if the open one was clicked
      if (activeNewsItemId === id) {
        return dispatch(markNewsItemAsActive({ id: null, columnId: null }));
      }

      dispatch(markNewsItemAsActive({ id, columnId }));
    },
    [activeNewsItemId, dispatch]
  );

  const newsItemList = ({ showLabelTop = false }) => {
    return searchResults.data.map(item => (
      <NewsItem
        active={activeNewsItemId === item.id}
        columnId={"search-results"}
        item={item}
        key={`news-item-search-result-${item.id}`}
        read={readNewsItems.includes(item.id)}
        showDate={true}
        showLabelTop={showLabelTop}
        updated={false}
        withInlineArticleView={false}
        onClick={setActiveNewsItem}
      />
    ));
  };

  const loader = () => {
    return (
      <>
        <NewsItemSkeleton />
        <NewsItemSkeleton />
        <NewsItemSkeleton />
      </>
    );
  };

  return (
    <div className={style["search-results"]}>
      {modalNewsItem.id && (
        <ArticleViewModal article={modalNewsItem} onClose={() => dispatch(setModalNewsItem({}))} />
      )}
      <div
        className={classNames(style["column"], {
          [style["column--single"]]: !isMobile
        })}
      >
        <div className={style["column__headline"]}>
          <div className={style["column__headline_title"]}>
            {t("search:searchResults")}: {formatSearchResult(searchResults.totalResults)}
          </div>
          <Select
            className={style["column__headline__select"]}
            classNamePrefix={"column__headline__select"}
            isDisabled={searchResults.loading}
            isSearchable={false}
            options={sortOptions}
            value={selectedSortOption}
            onChange={e => handleSortChange(e.value)}
          />
        </div>
        {searchResults.loading ? (
          <div className={style["loading"]}>
            <LoadingSpinner />
          </div>
        ) : searchResults.data.length ? (
          <>
            {isMobile ? (
              <div
                className={style["column__scrollable"]}
                id={"column-search-results"}
                ref={columnScrollableRef}
              >
                <InfiniteScroll
                  dataLength={searchResults.data.length}
                  hasMore={searchResults.totalResults > searchResults.data.length}
                  loader={loader()}
                  next={() => loadMore()}
                  scrollableTarget={"column-search-results"}
                >
                  {newsItemList({ showLabelTop: false })}
                </InfiniteScroll>
              </div>
            ) : (
              <div
                className={style["column__scrollable"]}
                id={"column-search-results"}
                ref={columnScrollableRef}
              >
                <InfiniteScroll
                  dataLength={searchResults.data.length}
                  hasMore={searchResults.totalResults > searchResults.data.length}
                  loader={loader()}
                  next={() => loadMore()}
                  scrollableTarget={"column-search-results"}
                >
                  {newsItemList({ showLabelTop: true })}
                </InfiniteScroll>
              </div>
            )}
          </>
        ) : (
          <div className={style["column__empty"]}>{t("search:emptySearchResults")}</div>
        )}
      </div>
      {activeNewsItem && (
        <ArticleView
          article={activeNewsItem}
          modal={true}
          searchString={filterSearchStringCollection.join(" OR ")}
          onClose={() => dispatch(markNewsItemAsActive({ id: null, columnId: null }))}
        />
      )}
    </div>
  );
}

CalendarSearchResults.propTypes = {
  filters: PropTypes.array
};

export default CalendarSearchResults;
